import React, { useState } from 'react';
import classNames from 'classnames';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import EmptyState from '@atlaskit/empty-state';
import { AnalyticsEventSource, trackSearchEvent } from '@atlassian/temp-analytics';
//@ts-ignore
import PPCHeaderLight from '@css-xp/base/assets/img/ppc-header-light.png';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import {
  FetchDirectMatchesForSearchInPpcQuery,
  FetchDirectMatchesForSearchInPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  PPC_PAGE_TITLE,
  ENABLE_DESIGN_ITERATION_1,
  routePaths,
  ENABLE_ORG_BASED_SEARCH_FOR_SOLD_TO_ADDRESS,
  UUID_REGEX,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { EmailTable } from '@css-xp/ppc-email-table';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { Box, Text } from '@atlaskit/primitives';
import { AppContent, AppPage, TabPanel, Tabs, useApplicationContext, useFeatureToggle } from '@css-xp/base';
import {
  SearchContextContainer,
  SearchData,
  SearchForm,
  SearchInputContainer,
  SearchContextSelectItem,
  SearchContextsMap,
  searchFieldKey,
  searchContextKey,
} from '@css-xp/ppc-search-form';
import isEmail from 'validator/lib/isEmail';
import { SearchSection } from '@css-xp/ppc-search-section';
import { PPC_TABS, ScreenEventAnalytics } from '@css-xp/ppc-common';
import { useTitle, useTotalCountQuery } from '@css-xp/ppc-custom-hooks';
import { PageBackgroundV2 } from '@css-xp/page-background';
import { searchPageInitialEmptyState } from '@css-xp/ppc-common/src/state-props';
import { getRouteFromDirectMatch } from '@css-xp/ppc-utils';
import { SearchPageLoader } from '@css-xp/ppc-page-loaders';
import { useFetchIdsFromContactEmail } from '@css-xp/contact-resources';
import { QuoteStatus } from '@atlassian/cssxp-types-billing';
import { fetchDirectMatchesForSearchInPPC } from '../queries/fetch-direct-match-for-search-ppc';
import { EndCustomerContactSearch } from './components/end-customer-contacts-search';
import { InvoiceSearchWithContext } from './components/invoice-search';
import { RefundSearch } from './components/refunds-search';
import { SiteUrlSearch } from './components/site-urls-search';
import { SoldToAddressesSearch } from './components/sold-to-addresses-search';
import { SubscriptionsSearch } from './components/subscriptions-search';
import { OrdersSearch } from './components/orders-search';
import { QuotesSearch } from './components/quotes-search';
import { BillingProfilesSearch } from './components/billing-profiles-search';
import { OrganisationSearch } from './components/organisation-search';
import './search-page.scss';

const getSkeletonLoader = () => {
  return (
    <SkeletonLoaderWrapper>
      <SkeletonLoader width="15%" marginBottom="0" />
    </SkeletonLoaderWrapper>
  );
};

export const getVariablesForQuotes = async ({
  searchTerm,
  transactionAccountId,
  getResourceIdsFilterOptions,
}: {
  searchTerm: string;
  transactionAccountId: string;
  getResourceIdsFilterOptions?: (
    _searchTerm: string
  ) => Promise<
    | {
        key: string;
        values: any;
      }[]
    | null
  >;
}) => {
  const isValidEmail = isEmail(searchTerm);

  const getFilterOptions = async () => {
    let contactEmailFilter = null;
    if (isValidEmail) {
      contactEmailFilter = await getResourceIdsFilterOptions?.(searchTerm);
    }

    return contactEmailFilter;
  };

  const filterOptions = {
    and: [
      {
        key: 'sourceSystem',
        value: 'CCP',
      },
      {
        key: 'transactionAccountId',
        value: transactionAccountId,
      },
    ],
    or: isValidEmail
      ? getFilterOptions()
      : [
          {
            key: 'shipToAddress1',
            value: searchTerm,
          },
          {
            key: 'quoteDescription',
            value: searchTerm,
          },
          {
            key: 'id',
            value: searchTerm,
          },
          {
            key: 'quoteNumber',
            value: searchTerm,
          },
          {
            key: 'organisationName',
            value: searchTerm,
          },
        ],
    not: [
      {
        key: 'status',
        value: QuoteStatus.DRAFT,
      },
      {
        key: 'status',
        value: QuoteStatus.DELETED,
      },
    ],
  };

  return {
    searchOptions: {
      first: 1,
    },
    filterOptions,
  };
};

export const SearchPage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  const history = useHistory();
  useTitle(PPC_PAGE_TITLE.HOME);
  // FIXME :: Upgrade react-router-dom to v6 to leverage useSearchParams hook
  const searchParams = new URLSearchParams(window.location.search);
  const hashPath = window.location.hash;
  const queryParam = searchParams.get(searchFieldKey) ?? '';
  const contextParam = searchParams.get(searchContextKey) ?? '';
  const [searchInputPlaceHolder, setSearchInputPlaceHolder] = useState<string | undefined>(
    SearchContextsMap.get(contextParam as generatedTypesNew.PartnerSearchContext)?.placeHolderText ?? undefined
  );
  const [isEnabled] = useFeatureToggle();
  const partnerData = useApplicationContext();
  const { getResourceIdsFilterOptions } = useFetchIdsFromContactEmail({
    transactionAccountId: txaId,
  });
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isOrgBasedSearchForSoldToAddressesEnabled = isEnabled(ENABLE_ORG_BASED_SEARCH_FOR_SOLD_TO_ADDRESS);
  const isAddOnsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS);

  const isEmailsImprovementEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_EMAILS_IMPROVEMENT);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isNonCommercialPricingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING);

  const selectedPartnerAccountData = partnerData.partnerAccounts.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  );

  const { totalCount, totalCountPerTab, totalCountLoading } = useTotalCountQuery({
    partnerAccountId: selectedPartnerAccountData?.id,
    isDesignIteration1Enabled,
    getTabsQueryVariables: {
      [PPC_TABS.QUOTES]: () =>
        getVariablesForQuotes({
          searchTerm: queryParam,
          transactionAccountId: txaId,
          getResourceIdsFilterOptions,
        }),
    },
  });

  const { data, loading } = useQuery<
    FetchDirectMatchesForSearchInPpcQuery,
    FetchDirectMatchesForSearchInPpcQueryVariables
  >(fetchDirectMatchesForSearchInPPC, {
    variables: {
      query: queryParam,
      filterOptions: [
        {
          key: 'activeSiteForMultipleSiteIds',
          value: '1',
        },
      ],
    },
    skip: !queryParam,
  });

  React.useEffect(() => {
    if (data && data.searchForDirectMatch) {
      const pathRulesConfig = {
        hasOrderPath: true,
        hasQuotesPath: true,
      };
      const isEnableOrgSearch = data.searchForDirectMatch?.entity === generatedTypesNew.GraphEntity.ORGANIZATION;
      const { pathToRedirectTo, isValidDirectMatch } = getRouteFromDirectMatch(
        data.searchForDirectMatch,
        txaId,
        contextParam,
        pathRulesConfig
      );
      // check whether entity is ORGANISATION then check if it is not org name search then redirect
      if (isEnableOrgSearch) {
        const isOrgNameSearch = !queryParam?.match(UUID_REGEX);
        // Check to redirect only in case of Org id search
        if (isValidDirectMatch && !isOrgNameSearch) {
          history.push(pathToRedirectTo);
        }
      } else {
        if (isValidDirectMatch) {
          history.push(pathToRedirectTo);
        }
      }
    }
  }, [contextParam, data, history, txaId]);

  const onSearchContextSelected = (context: SearchContextSelectItem) => {
    setSearchInputPlaceHolder(context.placeHolderText);
    searchParams.set(searchContextKey, context.value);
    history.push(`${window.location.pathname}?${searchParams.toString()}${hashPath}`);
  };

  const onSearchSubmit = (data: SearchData) => {
    searchParams.set(searchFieldKey, data[searchFieldKey]);
    searchParams.set(searchContextKey, data[searchContextKey]);
    history.push(`${window.location.pathname}?${searchParams.toString()}${hashPath}`);
    trackSearchEvent({
      selectedContext: data.context,
    });
  };

  const renderSubscriptionResults = () => {
    return (
      <SubscriptionsSearch
        query={queryParam}
        transactionAccountId={txaId}
        partnerSearchContext={contextParam as generatedTypesNew.PartnerSearchContext}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
        isAddOnsEnabled={isAddOnsEnabled}
        isSupportForExternallyBilledSubscriptionsEnabled={true}
        {...(isDesignIteration1Enabled && { version: 2 })}
        isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
        isNonCommercialPricingEnabled={isNonCommercialPricingEnabled}
      />
    );
  };

  const renderInvoiceResults = () => {
    return (
      <InvoiceSearchWithContext
        query={queryParam}
        transactionAccountId={txaId}
        partnerSearchContext={contextParam as generatedTypesNew.PartnerSearchContext}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  };
  const renderRefundResults = () => {
    return (
      <RefundSearch
        searchTerm={queryParam}
        partnerSearchContext={contextParam}
        transactionAccountId={txaId}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  };

  const renderSoldToAddressResults = () => {
    return (
      <SoldToAddressesSearch
        searchTerm={queryParam}
        partnerSearchContext={contextParam}
        transactionAccountId={txaId}
        isOrgSearchEnabled={isOrgBasedSearchForSoldToAddressesEnabled}
      />
    );
  };
  const renderOrganisationResults = () => {
    return <OrganisationSearch searchTerm={queryParam} transactionAccountId={txaId} />;
  };
  const renderSiteUrlsResults = () => {
    return (
      <SiteUrlSearch
        query={queryParam}
        partnerSearchContext={contextParam}
        transactionAccountId={txaId}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  };

  const renderCustomerContactsResults = () => {
    return (
      <EndCustomerContactSearch query={queryParam} partnerSearchContext={contextParam} transactionAccountId={txaId} />
    );
  };

  const renderEmailsResults = () => {
    return (
      <EmailTable
        searchQuery={queryParam}
        partnerAccountId={selectedPartnerAccountData?.id}
        transactionAccountId={txaId}
        hideFilters={true}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
        isEmailsImprovementEnabled={isEmailsImprovementEnabled}
      />
    );
  };

  const renderOrdersResults = () => {
    return (
      <OrdersSearch
        transactionAccountId={txaId}
        searchTerm={queryParam}
        directMatchEntity={data?.searchForDirectMatch?.entity ?? ''}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  };

  const renderQuotesResults = () => {
    return <QuotesSearch searchTerm={queryParam} transactionAccountId={txaId} />;
  };

  const renderBillingProfileResults = () => {
    return (
      <BillingProfilesSearch
        searchTerm={queryParam}
        transactionAccountId={txaId}
        partnerSearchContext={contextParam}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  };

  const getSearchHeader = () => {
    return (
      <SearchSection>
        <SearchForm onSubmit={onSearchSubmit} isRefetchOnClearEnabled={true}>
          {({ onReset }) => (
            <>
              <SearchContextContainer defaultValue={contextParam} onChange={onSearchContextSelected} />
              <SearchInputContainer
                defaultValue={queryParam}
                placeHolderText={searchInputPlaceHolder}
                isClearable
                onReset={onReset}
              />
            </>
          )}
        </SearchForm>
      </SearchSection>
    );
  };

  const getLoader = () => {
    if (isDesignIteration1Enabled) {
      return <SearchPageLoader />;
    }
    return <LoadingIndicator />;
  };

  const resultsTabs: { [entityType: string]: () => React.ReactNode } = {
    subscriptions: renderSubscriptionResults,
    invoices: renderInvoiceResults,
    refunds: renderRefundResults,
    soldToAddresses: renderSoldToAddressResults,
    organisation: renderOrganisationResults,
    siteUrls: renderSiteUrlsResults,
    customerContacts: renderCustomerContactsResults,
    emails: renderEmailsResults,
    orders: renderOrdersResults,
    quotes: renderQuotesResults,
    billingProfiles: renderBillingProfileResults,
  };

  const resultsTabsV2 = [
    {
      tabName: PPC_TABS.SUBSCRIPTIONS,
      getResults: resultsTabs['subscriptions'],
    },
    {
      tabName: PPC_TABS.QUOTES,
      getResults: resultsTabs['quotes'],
    },
    {
      tabName: PPC_TABS.INVOICES,
      getResults: resultsTabs['invoices'],
    },
    {
      tabName: PPC_TABS.REFUNDS,
      getResults: resultsTabs['refunds'],
    },
    {
      tabName: PPC_TABS.BILLING_PROFILES,
      getResults: resultsTabs['billingProfiles'],
    },
    {
      tabName: PPC_TABS.SITES,
      getResults: resultsTabs['siteUrls'],
    },
    {
      tabName: PPC_TABS.EMAILS,
      getResults: resultsTabs['emails'],
    },
    {
      tabName: PPC_TABS.ORDERS,
      getResults: resultsTabs['orders'],
    },
  ];

  const renderTabsV2 = () => (
    // Removed the totalCountPerTab prop as the count display for subscription tab is N/A from CCP
    // <Tabs testId={'ppc-search-results-tabs'} totalCountPerTab={isDesignIteration1Enabled && totalCountPerTab}>
    <Tabs testId={'ppc-search-results-tabs'}>
      {resultsTabsV2.reduce((init, resultTab) => {
        const { tabName, getResults } = resultTab;

        return [
          ...init,
          <TabPanel key={tabName} label={tabName}>
            {getResults()}
          </TabPanel>,
        ];
      }, [] as JSX.Element[])}
    </Tabs>
  );

  const renderTabs = () => (
    <Tabs testId="ppc-search-results-tabs">
      <TabPanel label={PPC_TABS.SUBSCRIPTIONS}> {resultsTabs['subscriptions']()} </TabPanel>
      <TabPanel label={PPC_TABS.INVOICES}> {resultsTabs['invoices']()} </TabPanel>
      <TabPanel label={PPC_TABS.REFUNDS}> {resultsTabs['refunds']()} </TabPanel>
      <TabPanel label={PPC_TABS.SOLD_TO_ADDRESS}> {resultsTabs['soldToAddresses']()} </TabPanel>
      <TabPanel label={PPC_TABS.ORGANISATION}> {resultsTabs['organisation']()} </TabPanel>
      <TabPanel label={PPC_TABS.SITE_URLS}> {resultsTabs['siteUrls']()} </TabPanel>
      <TabPanel label={PPC_TABS.CUSTOMER_CONTACTS}> {resultsTabs['customerContacts']()} </TabPanel>
      <TabPanel label={PPC_TABS.EMAILS}> {resultsTabs['emails']()} </TabPanel>
      <TabPanel label={PPC_TABS.ORDERS}> {resultsTabs['orders']()} </TabPanel>
      <TabPanel label={PPC_TABS.QUOTES}> {resultsTabs['quotes']()} </TabPanel>
      <TabPanel label={PPC_TABS.BILLING_PROFILES}> {resultsTabs['billingProfiles']()} </TabPanel>
    </Tabs>
  );

  const renderResultTabs = () => {
    if (isDesignIteration1Enabled) {
      return renderTabsV2();
    }
    return renderTabs();
  };

  const SearchPageSection = (
    <AppPage
      className={classNames({
        'app-page-v2 gray-bg ppc-search-page': !isDesignIteration1Enabled,
        'ppc-search-page-v2': isDesignIteration1Enabled,
      })}
    >
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.PARTNER_SEARCH_SCREEN,
          attributes: {},
        }}
      />

      {isDesignIteration1Enabled ? (
        <PageBackgroundV2
          heading={
            <>
              Search results
              {/*TODO: Uncomment this once the total count is available for all tabs*/}
              {/* {!!queryParam && !totalCountLoading && !!totalCount && (
                <Text color="color.text.subtle">
                  <br /> {totalCount.toLocaleString()} {totalCount === 1 ? 'result' : 'results'} found for {queryParam}
                </Text>
              )}
              {!!queryParam && totalCountLoading && <Box paddingBlockStart="space.200">{getSkeletonLoader()}</Box>} */}
            </>
          }
          breadcrumbsProps={{
            items: [
              {
                title: 'Home',
                href: routePaths.home,
              },
              {
                title: 'Search results',
                href: `${routePaths.search}?${searchParams.toString()}${hashPath}`,
              },
            ],
          }}
          className="search-page-header-v2"
        />
      ) : (
        <div className="search-page-header">
          <img src={PPCHeaderLight} />
          {getSearchHeader()}
        </div>
      )}

      <AppContent className={'ppc-search-app-content'}>
        {loading || (queryParam && totalCountLoading) ? (
          getLoader()
        ) : queryParam ? (
          renderResultTabs()
        ) : isDesignIteration1Enabled ? (
          <EmptyState {...searchPageInitialEmptyState} />
        ) : (
          <EmptyState header="No results to display" />
        )}
      </AppContent>
    </AppPage>
  );

  return SearchPageSection;
};
